import React, { useState } from 'react';
import { colors, radius, fontStack } from '../design-system/tokens';

const PRIORITY_COLOR = {
  high:   'var(--danger)',
  medium: 'var(--warning)',
  low:    'var(--success)',
};

/**
 * TaskRow — checkbox + title + meta row + ... menu.
 *
 * Props:
 *   - task: { id, title, priority, category, estimated_duration_min, is_completed }
 *   - onToggle(task)
 *   - onEdit(task)?
 *   - onDelete(task)?
 *   - onRollover(task)?    optional "push to tomorrow"
 */
export default function TaskRow({ task, onToggle, onEdit, onDelete, onRollover }) {
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const completed = !!task.is_completed;

  return (
    <li
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setMenuOpen(false); }}
      style={{
        listStyle: 'none',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 12px',
        borderRadius: radius.sm,
        backgroundColor: hover ? 'var(--bg-hover)' : 'transparent',
        transition: 'background-color 120ms ease',
        position: 'relative',
        fontFamily: fontStack,
      }}
    >
      {/* Checkbox */}
      <button
        type="button"
        onClick={() => onToggle && onToggle(task)}
        aria-pressed={completed}
        aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
        style={{
          flexShrink: 0,
          width: 18,
          height: 18,
          marginTop: 2,
          padding: 0,
          background: completed ? colors.primary : '#fff',
          border: `1.5px solid ${completed ? colors.primary : colors.border}`,
          borderRadius: 4,
          cursor: 'pointer',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          transition: 'all 120ms ease',
        }}
      >
        {completed ? '✓' : ''}
      </button>

      {/* Title + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: completed ? colors.textSecondary : colors.textPrimary,
            textDecoration: completed ? 'line-through' : 'none',
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {task.title}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
            fontSize: 11,
            color: colors.textSecondary,
          }}
        >
          {task.priority && (
            <Pill
              text={task.priority}
              color={PRIORITY_COLOR[task.priority] || PRIORITY_COLOR.medium}
            />
          )}
          {task.category && (
            <span style={{ textTransform: 'capitalize' }}>{task.category}</span>
          )}
          {task.estimated_duration_min ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{task.estimated_duration_min} min</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      {(onEdit || onDelete || onRollover) && (hover || menuOpen) && (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Task actions"
            style={{
              width: 28,
              height: 28,
              padding: 0,
              border: 'none',
              background: 'transparent',
              borderRadius: 999,
              cursor: 'pointer',
              color: colors.textSecondary,
              fontSize: 18,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ⋯
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: 4,
                background: '#fff',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                padding: 4,
                minWidth: 160,
                zIndex: 10,
                fontSize: 13,
              }}
            >
              {onEdit && (
                <MenuItem onClick={() => { setMenuOpen(false); onEdit(task); }}>Edit</MenuItem>
              )}
              {onRollover && !completed && (
                <MenuItem onClick={() => { setMenuOpen(false); onRollover(task); }}>
                  Push to tomorrow
                </MenuItem>
              )}
              {onDelete && (
                <MenuItem
                  destructive
                  onClick={() => { setMenuOpen(false); onDelete(task); }}
                >
                  Delete
                </MenuItem>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function MenuItem({ onClick, children, destructive }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: 'none',
        background: 'transparent',
        borderRadius: 6,
        color: destructive ? 'var(--danger)' : colors.textPrimary,
        cursor: 'pointer',
        fontFamily: fontStack,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {children}
    </button>
  );
}

function Pill({ text, color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        backgroundColor: hexAlpha(color, 0.12),
        color: color,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'capitalize',
        letterSpacing: '0.02em',
      }}
    >
      {text}
    </span>
  );
}

function hexAlpha(hex, alpha) {
  // Phase 12: also accept `var(--xxx)` so callers can pass theme tokens.
  // We rely on `color-mix` (modern Chromium 111+, Safari 16.2+, FF 113+)
  // when the input isn't a parseable hex. Keeps tints theme-aware in
  // both modes; falls back to the raw value if both paths fail.
  if (typeof hex === 'string' && hex.startsWith('var(')) {
    const pct = Math.round(alpha * 100);
    return `color-mix(in srgb, ${hex} ${pct}%, transparent)`;
  }
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1].slice(0, 2), 16);
  const g = parseInt(m[1].slice(2, 4), 16);
  const b = parseInt(m[1].slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
